/**
 * Loads the placeholder a part was created from (if any) and keeps the drawer's selector refs' display
 * names in sync with it.
 *
 * A part stores only ids for its facility sub-type / accommodation category / equipment; the human names
 * may be missing (e.g. right after a drop). This hook fetches the bound placeholder and backfills any
 * missing names — but never overwrites a name the user already edited, and only when the id still matches.
 * PartDetailForm is keyed by part uid, so this hook's local state is seeded once per opened part.
 */
import { useEffect, useState } from 'react';

import { useGetProductsPlaceholder } from '@/api/generated/products-placeholders/products-placeholders';
import type { BoardPart } from '../model';
import type { CodebookLikeRef, LegacyRef } from './drawerSelectors';

export function usePlaceholderBackfill(part: BoardPart) {
  const [facilityRef, setFacilityRef] = useState<LegacyRef | null>(
    part.facilitySubTypeId != null
      ? { id: part.facilitySubTypeId, name: part.facilitySubTypeName ?? '' }
      : null
  );
  const [categoryRef, setCategoryRef] = useState<CodebookLikeRef | null>(
    part.accommodationCategoryCbId != null
      ? {
          id: part.accommodationCategoryCbId,
          name: part.accommodationCategoryName ?? '',
          code: null,
        }
      : null
  );
  const [equipmentRefs, setEquipmentRefs] = useState<LegacyRef[]>(
    part.equipmentIds.map((id) => ({ id, name: '' }))
  );

  const placeholderQuery = useGetProductsPlaceholder(part.sourcePlaceholderId ?? '', {
    query: { enabled: Boolean(part.sourcePlaceholderId) },
  });
  const placeholder = placeholderQuery.data;

  // Backfill selector display names from the bound placeholder once it loads (never overwrites edits).
  useEffect(() => {
    if (!placeholder) return;
    setFacilityRef((prev) =>
      prev && !prev.name && placeholder.facilitySubTypeId === prev.id
        ? { ...prev, name: placeholder.facilitySubTypeName ?? '' }
        : prev
    );
    setCategoryRef((prev) =>
      prev && !prev.name && placeholder.accommodationCategoryCbId === prev.id
        ? { ...prev, name: placeholder.accommodationCategoryName ?? '' }
        : prev
    );
    setEquipmentRefs((prev) =>
      prev.map((ref) => {
        if (ref.name) return ref;
        const match = placeholder.equipment.find((eq) => eq.id === ref.id);
        return match ? { id: ref.id, name: match.name ?? '' } : ref;
      })
    );
  }, [placeholder]);

  return {
    placeholder,
    facilityRef,
    setFacilityRef,
    categoryRef,
    setCategoryRef,
    equipmentRefs,
    setEquipmentRefs,
  };
}
